import React, {useState, useEffect} from "react"
import {OrgSettings, update} from "../../resources/orgSettings"
import {useAccessToken} from "../../services/auth"
import {useToasts} from "react-toast-notifications"
import "./_orgSettings.sass"
import Button from "../../components/Button/Button"
import useAsyncResultIdle from "../../hooks/useAsyncResultIdle"

// Custom chip component to match the design in the screenshot
const TagChip = ({label, onRemove}: {label: string; onRemove: () => void}) => {
	return (
		<div className="customer-tag-inheritance-chip">
			<span className="customer-tag-inheritance-chip-label">{label}</span>
			<button
				type="button"
				className="customer-tag-inheritance-chip-delete"
				onClick={onRemove}
				aria-label={`Remove ${label}`}
			>
				×
			</button>
		</div>
	)
}

export default function CustomerTagInheritanceSettings({
	orgSettings,
	refresh,
}: {
	orgSettings: OrgSettings
	refresh: () => void
}) {
	const accessToken = useAccessToken()
	const {addToast} = useToasts()
	const [cardsTagKeysInput, setCardsTagKeysInput] = useState<string>("")
	const [accountsTagKeysInput, setAccountsTagKeysInput] = useState<string>("")
	const [updatedCardsTagKeys, setUpdatedCardsTagKeys] = useState<string[]>(
		orgSettings.attributes.customerTagInheritancePolicy?.keysToInheritForCards || []
	)
	const [updatedAccountsTagKeys, setUpdatedAccountsTagKeys] = useState<string[]>(
		orgSettings.attributes.customerTagInheritancePolicy?.keysToInheritForAccounts || []
	)
	const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)

	const [state, patch] = useAsyncResultIdle(update)

	const isLoading = state.match(
		() => false,
		() => true,
		() => false,
		() => false
	)

	// Check if there are unsaved changes by comparing with original data
	useEffect(() => {
		const originalCardsKeys = orgSettings.attributes.customerTagInheritancePolicy?.keysToInheritForCards || []
		const originalAccountsKeys = orgSettings.attributes.customerTagInheritancePolicy?.keysToInheritForAccounts || []

		const cardsChanged = JSON.stringify(originalCardsKeys.sort()) !== JSON.stringify(updatedCardsTagKeys.sort())
		const accountsChanged =
			JSON.stringify(originalAccountsKeys.sort()) !== JSON.stringify(updatedAccountsTagKeys.sort())

		setHasUnsavedChanges(cardsChanged || accountsChanged)
	}, [updatedCardsTagKeys, updatedAccountsTagKeys, orgSettings])

	const addCardTag = () => {
		if (cardsTagKeysInput.trim() !== "" && !updatedCardsTagKeys.includes(cardsTagKeysInput.trim())) {
			setUpdatedCardsTagKeys([...updatedCardsTagKeys, cardsTagKeysInput.trim()])
			setCardsTagKeysInput("")
		}
	}

	const removeCardTag = (tag: string) => {
		setUpdatedCardsTagKeys(updatedCardsTagKeys.filter((t) => t !== tag))
	}

	const addAccountTag = () => {
		if (accountsTagKeysInput.trim() !== "" && !updatedAccountsTagKeys.includes(accountsTagKeysInput.trim())) {
			setUpdatedAccountsTagKeys([...updatedAccountsTagKeys, accountsTagKeysInput.trim()])
			setAccountsTagKeysInput("")
		}
	}

	const removeAccountTag = (tag: string) => {
		setUpdatedAccountsTagKeys(updatedAccountsTagKeys.filter((t) => t !== tag))
	}

	const createPatchObject = () => {
		return {
			customerTagInheritancePolicy: {
				keysToInheritForCards: updatedCardsTagKeys,
				keysToInheritForAccounts: updatedAccountsTagKeys,
			},
		}
	}

	// This saves the changes to the server
	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault()

		try {
			await patch(accessToken, createPatchObject())
			addToast("Customer Tag Inheritance Settings Updated Successfully", {appearance: "success"})
			setHasUnsavedChanges(false)
			refresh()
		} catch (error) {
			addToast("Failed to update settings", {appearance: "error"})
		}
	}

	return (
		<div className="customer-tag-inheritance">
			<form onSubmit={handleSubmit}>
				<h3 className="title is-4">Customer Tag Inheritance</h3>
				<p className="customer-tag-inheritance-description">
					Configure which customer tags should be inherited by cards and accounts. These settings allow customer tags to
					be automatically applied to new cards and accounts.
				</p>

				<div className="customer-tag-inheritance-section">
					<h4 className="customer-tag-inheritance-section-title">Card Tag Inheritance</h4>
					<p className="customer-tag-inheritance-section-description">
						These customer tag keys will be inherited by new cards.
					</p>

					<div className="customer-tag-inheritance-tag-input">
						<label className="customer-tag-inheritance-tag-input-label">Tag Keys</label>
						<div className="customer-tag-inheritance-tag-input-container">
							<div className="customer-tag-inheritance-input-wrapper">
								<input
									className="input customer-tag-inheritance-tag-input-field"
									type="text"
									placeholder="Enter tag key"
									value={cardsTagKeysInput}
									onChange={(e) => setCardsTagKeysInput(e.target.value)}
									onKeyDown={(e) => {
										if (e.key === "Enter") {
											e.preventDefault()
											addCardTag()
										}
									}}
								/>
							</div>
							<Button
								theme="neutral"
								prefixIcon="add-plus-small"
								onClick={addCardTag}
								className="customer-tag-inheritance-add-button"
								type="button" // Explicitly set type to button to avoid form submission
							>
								Add
							</Button>
						</div>
					</div>

					<div className="customer-tag-inheritance-tags">
						{updatedCardsTagKeys.length > 0 ? (
							<div className="customer-tag-inheritance-chips-container">
								{updatedCardsTagKeys.map((tag) => (
									<TagChip key={tag} label={tag} onRemove={() => removeCardTag(tag)} />
								))}
							</div>
						) : (
							<p className="customer-tag-inheritance-no-tags">No tag keys added</p>
						)}
					</div>
				</div>

				<div className="customer-tag-inheritance-section">
					<h4 className="customer-tag-inheritance-section-title">Account Tag Inheritance</h4>
					<p className="customer-tag-inheritance-section-description">
						These customer tag keys will be inherited by new accounts.
					</p>

					<div className="customer-tag-inheritance-tag-input">
						<label className="customer-tag-inheritance-tag-input-label">Tag Keys</label>
						<div className="customer-tag-inheritance-tag-input-container">
							<div className="customer-tag-inheritance-input-wrapper">
								<input
									className="input customer-tag-inheritance-tag-input-field"
									type="text"
									placeholder="Enter tag key"
									value={accountsTagKeysInput}
									onChange={(e) => setAccountsTagKeysInput(e.target.value)}
									onKeyDown={(e) => {
										if (e.key === "Enter") {
											e.preventDefault()
											addAccountTag()
										}
									}}
								/>
							</div>
							<Button
								theme="neutral"
								prefixIcon="add-plus-small"
								onClick={addAccountTag}
								className="customer-tag-inheritance-add-button"
								type="button" // Explicitly set type to button to avoid form submission
							>
								Add
							</Button>
						</div>
					</div>

					<div className="customer-tag-inheritance-tags">
						{updatedAccountsTagKeys.length > 0 ? (
							<div className="customer-tag-inheritance-chips-container">
								{updatedAccountsTagKeys.map((tag) => (
									<TagChip key={tag} label={tag} onRemove={() => removeAccountTag(tag)} />
								))}
							</div>
						) : (
							<p className="customer-tag-inheritance-no-tags">No tag keys added</p>
						)}
					</div>
				</div>

				<div className="customer-tag-inheritance-footer">
					<Button type="submit" theme="success" loading={isLoading} disabled={isLoading || !hasUnsavedChanges}>
						Save Changes
					</Button>
				</div>
			</form>
		</div>
	)
}
